import { useMemo } from 'react';
import {
  buildColumns,
  filterOptions,
  findOptionPath,
  formatLocaleOptions,
  getDefaultActivePath,
  getPathValues,
} from './utils';

import type { TFormatMessage } from './utils';
import type { ICascaderSelectChangeContext, ICascaderSelectOption } from './index';

interface IUseCascaderSelectParams {
  formatMessage: TFormatMessage;
  options?: ICascaderSelectOption[] | null;
  value?: string;
  search: string;
  activePath: string[];
}

export default function useCascaderSelect(params: IUseCascaderSelectParams) {
  const { formatMessage, options, value, search, activePath } = params;

  const localeOptions = useMemo(() => {
    return formatLocaleOptions(formatMessage, options);
  }, [formatMessage, options]);

  const selectedResult = useMemo(() => {
    return findOptionPath(localeOptions, value);
  }, [localeOptions, value]);

  const realOptions = useMemo(() => {
    return filterOptions(localeOptions, search);
  }, [localeOptions, search]);

  const realActivePath = useMemo(() => {
    if (Array.isArray(activePath) && activePath.length > 0) {
      return activePath;
    }

    if (selectedResult?.path?.length > 0) {
      return getPathValues(selectedResult.path);
    }

    return getDefaultActivePath(realOptions);
  }, [activePath, realOptions, selectedResult?.path]);

  const columns = useMemo(() => {
    return buildColumns(realOptions, realActivePath);
  }, [realOptions, realActivePath]);

  const currentContext = useMemo<ICascaderSelectChangeContext>(() => {
    return {
      selectedOption: selectedResult?.option,
      parentOption: selectedResult?.parentOption,
      path: selectedResult?.path || [],
    };
  }, [selectedResult]);

  return {
    columns,
    activePath: realActivePath,
    selectedResult,
    currentContext,
  };
}
